import "./ProductionSummary.css";
import { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";


function ProductionSummary({ open, onClose }) {

  // ================= DRAG STATE =================
  const [sharing, setSharing] = useState(false);

  const [pos, setPos] = useState({
    x: 260,
    y: 120
  });

  const dragging = useRef(false);

  const offset = useRef({
    x: 0,
    y: 0
  });

  const startDrag = (e) => {

    dragging.current = true;

    offset.current = {
      x: e.clientX - pos.x,
      y: e.clientY - pos.y
    };
  };

  const onDrag = (e) => {

    if (!dragging.current) return;

    setPos({
      x: e.clientX - offset.current.x,
      y: e.clientY - offset.current.y
    });
  };

  const stopDrag = () => {
    dragging.current = false;
  };

  // ================= LIVE DATA =================
  const [data, setData] = useState({

    nk1: 0,
    nk2: 0,
    nk3: 0,

    crude: 0,
    gas: 0,
    flare: 0,
    water: 0,

    totalNk1: 0,
    totalNk2: 0,
    totalNk3: 0,

    totalCrude: 0,
    totalGas: 0,
    totalFlare: 0,
    totalWater: 0

  });

  // ================= TREND STORAGE =================
  const trend = useRef({
    nk1: [],
    nk2: [],
    nk3: [],
    crude: [],
    gas: [],
    flare: [],
    water: []
  });

  // ================= LIVE UPDATE ENGINE =================
  useEffect(() => {

    // ==========================================
    // LOAD SAVED TOTALIZERS
    // ==========================================

    setData(prev => ({
      ...prev,

      totalNk1:
        Number(localStorage.getItem("totalNk1")) || 0,

      totalNk2:
        Number(localStorage.getItem("totalNk2")) || 0,

      totalNk3:
        Number(localStorage.getItem("totalNk3")) || 0,

      totalCrude:
        Number(localStorage.getItem("totalCrude")) || 0,

      totalGas:
        Number(localStorage.getItem("totalGas")) || 0,

      totalFlare:
        Number(localStorage.getItem("totalFlare")) || 0,

      totalWater:
        Number(localStorage.getItem("totalWater")) || 0
    }));

    // ==========================================
    // MAIN SCAN CYCLE
    // ==========================================

    const interval = setInterval(() => {

      // ======================================
      // DIRECT LIVE FETCH FROM LOCAL STORAGE
      // ======================================

      const nk1 =
        Number(localStorage.getItem("fic10103")) || 0;

      const nk2 =
        Number(localStorage.getItem("fic10203")) || 0;

      const nk3 =
        Number(localStorage.getItem("fic10303")) || 0;

      const fic16006 =
        Number(localStorage.getItem("FIC_16006")) || 0;

      const fic16007 =
        Number(localStorage.getItem("FIC_16007")) || 0;

      const fic2606 =
        Number(localStorage.getItem("FIC_2606")) || 0;

      const fic2607 =
        Number(localStorage.getItem("FIC_2607")) || 0;

      const flare =
        Number(localStorage.getItem("FIC_2983")) || 0;

      const water =
        Number(localStorage.getItem("FIC_2908")) || 0;

      // ======================================
      // EXPORT SELECTION LOGIC
      // ======================================

      const crude =
        fic16006 > 0 
          ? fic16006
          : fic16007;

      const gas =
        fic2606 > 0
          ? fic2606
          : fic2607;

      // ======================================
      // UPDATE DATA
      // ======================================

      setData(prev => {

        const updated = {

          nk1,
          nk2,
          nk3,

          crude,
          gas,
          flare,
          water,

          // ==================================
          // TOTALIZER LOGIC
          // ==================================

          totalNk1:
            prev.totalNk1 + (nk1 / 60),

          totalNk2:
            prev.totalNk2 + (nk2 / 60),

          totalNk3:
            prev.totalNk3 + (nk3 / 60),

          totalCrude:
            prev.totalCrude + (crude / 60),

          totalGas:
            prev.totalGas + (gas / 60),

          totalFlare:
            prev.totalFlare + (flare / 60),

          totalWater:
            prev.totalWater + (water / 60)
        };

        // ==================================
        // SAVE TOTALIZER VALUES
        // ==================================

        localStorage.setItem(
          "totalNk1",
          updated.totalNk1
        );

        localStorage.setItem(
          "totalNk2",
          updated.totalNk2
        );

        localStorage.setItem(
          "totalNk3",
          updated.totalNk3
        );

        localStorage.setItem(
          "totalCrude",
          updated.totalCrude
        );

        localStorage.setItem(
          "totalGas",
          updated.totalGas
        );

        localStorage.setItem(
          "totalFlare",
          updated.totalFlare
        );

        localStorage.setItem(
          "totalWater",
          updated.totalWater
        );

        return updated;
      });

      // ======================================
      // TREND STORAGE
      // ======================================

      const push = (key, val) => {

        const arr = trend.current[key];

        arr.push(val);

        if (arr.length > 20) {
          arr.shift();
        }
      };

      push("nk1", nk1);
      push("nk2", nk2);
      push("nk3", nk3);

      push("crude", crude);
      push("gas", gas);

      push("flare", flare);
      push("water", water);

    }, 1000);

    return () => clearInterval(interval);

  }, []);

  // ================= CONVERSIONS =================

  const BOE_DAY = (v) => v * 1440;

  const MMSCFD = (v) => v * 1440;

  const LPD = (v) => v * 1440;

  // ================= BAR SCALE =================

  const scale = (v, max) =>
    Math.min((v / max) * 100, 100);

  // ================= EXPORT EXCEL =================

  const exportExcel = () => {

    const sheet = [

      ["Tag", "Rate/Min", "Rate/Day", "Accumulated"],

      [
        "NK-1",
        data.nk1,
        BOE_DAY(data.nk1),
        data.totalNk1
      ],

      [
        "NK-2",
        data.nk2,
        BOE_DAY(data.nk2),
        data.totalNk2
      ],

      [
        "NK-3",
        data.nk3,
        BOE_DAY(data.nk3),
        data.totalNk3
      ],

      [
        "Crude Export",
        data.crude,
        BOE_DAY(data.crude),
        data.totalCrude
      ],

      [
        "Gas Export",
        data.gas,
        MMSCFD(data.gas),
        data.totalGas
      ],

      [
        "Gas Flare",
        data.flare,
        MMSCFD(data.flare),
        data.totalFlare
      ],

      [
        "Produced Water",
        data.water,
        LPD(data.water),
        data.totalWater
      ]
    ];

    const ws = XLSX.utils.aoa_to_sheet(sheet);

    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      "Production Summary"
    );

    XLSX.writeFile(
      wb,
      "Production_Summary.xlsx"
    );
  };

// ================= PRINT =================

const printWindow = () => {

  const printContent =
    document.querySelector(".prod-window");

  if (!printContent) return;

  // REMOVE FOOTER + CLOSE BUTTON
  const cloned =
    printContent.cloneNode(true);

  const footer =
    cloned.querySelector(".prod-footer");

  if (footer) footer.remove();

  const closeBtn =
    cloned.querySelector(".prod-close");

  if (closeBtn) closeBtn.remove();

  const printWin =
    window.open("", "", "width=1400,height=1000");

  printWin.document.write(`

    <html>

      <head>

        <title>
          NK Tech Production Summary
        </title>

        <style>

          *{
            box-sizing:border-box;
          }

          body{
            margin:0;
            padding:30px;
            background:white;
            color:#111827;
            font-family:Arial,sans-serif;
          }

          .prod-window{
            width:100%;
          }

          /* ================= HEADER ================= */

          .prod-header{
            text-align:center;
            border-bottom:3px solid #0f172a;
            padding-bottom:20px;
            margin-bottom:30px;
          }

          .prod-title{
            font-size:28px;
            font-weight:bold;
            color:#0f172a;
            letter-spacing:1px;
          }

          .prod-subtitle{
            font-size:18px;
            margin-top:10px;
            color:#475569;
          }

          /* ================= BODY ================= */

          .prod-body{
            width:100%;
          }

          .prod-text{
            margin-bottom:25px;
            font-size:15px;
            line-height:1.6;
          }

          /* ================= ROWS ================= */

          .prod-row{
            border:1px solid #cbd5e1;
            border-radius:10px;
            padding:15px;
            margin-bottom:15px;
            background:#f8fafc;
            page-break-inside:avoid;
          }

          .prod-tag{
            font-size:18px;
            font-weight:bold;
            color:#0f172a;
            margin-bottom:10px;
          }

          .prod-row div{
            margin-bottom:6px;
            font-size:14px;
          }

          /* ================= BAR ================= */

          .prod-bar{
            width:100%;
            height:12px;
            background:#e2e8f0;
            border-radius:20px;
            overflow:hidden;
            margin-top:12px;
          }

          .prod-bar div{
            height:100%;
            background:#0ea5e9;
            border-radius:20px;
          }

          /* ================= DISCLAIMER ================= */

          .prod-disclaimer{
            margin-top:30px;
            border:1px solid #cbd5e1;
            padding:20px;
            border-radius:10px;
            background:#f8fafc;
            line-height:1.8;
            font-size:14px;
          }

          /* ================= PRINT CLEANUP ================= */

          button{
            display:none !important;
          }

          @media print{

            body{
              padding:10px;
            }

            .prod-row{
              break-inside:avoid;
            }

          }

        </style>

      </head>

      <body>

        ${cloned.outerHTML}

      </body>

    </html>

  `);

  printWin.document.close();

  printWin.focus();

  setTimeout(() => {

    printWin.print();

    printWin.close();

  }, 700);

};

  // ================= SHARE =================

 // ================= SHARE =================

const shareWindow = async () => {

  try {

    const report = `

NK TECH INDUSTRIAL DCS AND DIGITAL TWIN SOLUTION
PRODUCTION SUMMARY REPORT

----------------------------------------

NK-1
Rate: ${data.nk1.toFixed(2)} BOE/M
Daily: ${BOE_DAY(data.nk1).toFixed(2)} BOE/D
Total: ${data.totalNk1.toFixed(2)}

----------------------------------------

NK-2
Rate: ${data.nk2.toFixed(2)} BOE/M
Daily: ${BOE_DAY(data.nk2).toFixed(2)} BOE/D
Total: ${data.totalNk2.toFixed(2)}

----------------------------------------

NK-3
Rate: ${data.nk3.toFixed(2)} BOE/M
Daily: ${BOE_DAY(data.nk3).toFixed(2)} BOE/D
Total: ${data.totalNk3.toFixed(2)}

----------------------------------------

CRUDE EXPORT
Rate: ${data.crude.toFixed(2)} BPM
Daily: ${BOE_DAY(data.crude).toFixed(2)} BPD
Total: ${data.totalCrude.toFixed(2)}

----------------------------------------

GAS EXPORT
Rate: ${data.gas.toFixed(2)} MMSCF/M
Daily: ${MMSCFD(data.gas).toFixed(2)} MMSCFD
Total: ${data.totalGas.toFixed(2)}

----------------------------------------

GAS FLARE
Rate: ${data.flare.toFixed(2)} MMSCF/M
Daily: ${MMSCFD(data.flare).toFixed(2)} MMSCFD
Total: ${data.totalFlare.toFixed(2)}

----------------------------------------

PRODUCED WATER
Rate: ${data.water.toFixed(2)} LPM
Daily: ${LPD(data.water).toFixed(2)} LPD
Total: ${data.totalWater.toFixed(2)}

----------------------------------------

DISCLAIMER:
This production data is simulated for training,
process monitoring, and operational familiarization only.

Generated from:
NK TECH INDUSTRIAL DCS AND DIGITAL TWIN SOLUTION

`;

    // ================= NATIVE SHARE =================

    if (navigator.share) {

      await navigator.share({

        title:
          "NK Tech Production Summary",

        text: report

      });

    } else {

      // ================= FALLBACK =================

      await navigator.clipboard.writeText(report);

      alert(
        "Production Summary copied to clipboard."
      );
    }

  } catch (err) {

    console.log("Share Error:", err);

  }

};

  // ================= CLOSE CONDITION =================

  if (!open) return null;

  return (

    <div
      className="prod-overlay"
      onMouseMove={onDrag}
      onMouseLeave={stopDrag}
      onMouseUp={stopDrag}
    >

      <div
        className="prod-window"
        style={{
          left: pos.x,
          top: pos.y
        }}
      >

        {/* ================= HEADER ================= */}

        <div
          className="prod-header"
          onMouseDown={startDrag}
        >

          <div className="prod-title">
            NK TECH INDUSTRIAL DCS AND DIGITAL TWIN SOLUTION
          </div>

          <div className="prod-subtitle">
            PRODUCTION SUMMARY
          </div>

          <button
            className="prod-close"
            onClick={onClose}
          >
            ✕
          </button>

        </div>

        {/* ================= BODY ================= */}

        <div className="prod-body">

          <p className="prod-text">
            Live production summary based on
            operational DCS process signals.
          </p>

          {/* ================= WELLS ================= */}

          {[
            [
              "NK-1",
              data.nk1,
              data.totalNk1
            ],

            [
              "NK-2",
              data.nk2,
              data.totalNk2
            ],

            [
              "NK-3",
              data.nk3,
              data.totalNk3
            ]

          ].map(([tag, val, total]) => (

            <div
              className="prod-row"
              key={tag}
            >

              <div className="prod-tag">
                {tag}
              </div>

              <div>
                {val.toFixed(2)} BOE/M
              </div>

              <div>
                {BOE_DAY(val).toFixed(2)} BOE/D
              </div>

              <div>
                Total:
                {" "}
                {total.toFixed(2)}
              </div>

              <div className="prod-bar">
                <div
                  style={{
                    width:
                      `${scale(val, 100)}%`
                  }}
                />
              </div>

            </div>
          ))}

          {/* ================= CRUDE ================= */}

          <div className="prod-row">

            <div className="prod-tag">
              Crude Export
            </div>

            <div>
              {data.crude.toFixed(2)} BPM
            </div>

            <div>
              {BOE_DAY(data.crude).toFixed(2)} BPD
            </div>

            <div>
              Total:
              {" "}
              {data.totalCrude.toFixed(2)}
            </div>

            <div className="prod-bar">
              <div
                style={{
                  width:
                    `${scale(data.crude, 100)}%`
                }}
              />
            </div>

          </div>

          {/* ================= GAS ================= */}

          <div className="prod-row">

            <div className="prod-tag">
              Gas Export
            </div>

            <div>
              {data.gas.toFixed(2)} MMSCF/M
            </div>

            <div>
              {MMSCFD(data.gas).toFixed(2)} MMSCFD
            </div>

            <div>
              Total:
              {" "}
              {data.totalGas.toFixed(2)}
            </div>

            <div className="prod-bar">
              <div
                style={{
                  width:
                    `${scale(data.gas, 100)}%`
                }}
              />
            </div>

          </div>

          {/* ================= FLARE ================= */}

          <div className="prod-row">

            <div className="prod-tag">
              Gas Flare
            </div>

            <div>
              {data.flare.toFixed(2)} MMSCF/M
            </div>

            <div>
              {MMSCFD(data.flare).toFixed(2)} MMSCFD
            </div>

            <div>
              Total:
              {" "}
              {data.totalFlare.toFixed(2)}
            </div>

            <div className="prod-bar">
              <div
                style={{
                  width:
                    `${scale(data.flare, 50)}%`
                }}
              />
            </div>

          </div>

          {/* ================= WATER ================= */}

          <div className="prod-row">

            <div className="prod-tag">
              Produced Water
            </div>

            <div>
              {data.water.toFixed(2)} LPM
            </div>

            <div>
              {LPD(data.water).toFixed(2)} LPD
            </div>

            <div>
              Total:
              {" "}
              {data.totalWater.toFixed(2)}
            </div>

            <div className="prod-bar">
              <div
                style={{
                  width:
                    `${scale(data.water, 200)}%`
                }}
              />
            </div>

          </div>

          {/* ================= DISCLAIMER ================= */}

          <div className="prod-disclaimer">

            <strong>Disclaimer:</strong>

            {" "}

            This production data is simulated
            for training, process monitoring,
            and operational familiarization only.
            It does not represent certified
            fiscal metering or custody transfer
            values.

          </div>

        </div>

        {/* ================= FOOTER ================= */}

        <div className="prod-footer">

          <button onClick={exportExcel}>
            Export Excel
          </button>

          <button
            onClick={shareWindow}
            disabled={sharing}
          >
            {sharing
              ? "Sharing..."
              : "Share"}
          </button>

          <button onClick={printWindow}>
            Print PDF
          </button>

          <button
            onClick={() => {

              localStorage.removeItem("totalNk1");
              localStorage.removeItem("totalNk2");
              localStorage.removeItem("totalNk3");

              localStorage.removeItem("totalCrude");
              localStorage.removeItem("totalGas");
              localStorage.removeItem("totalFlare");
              localStorage.removeItem("totalWater");

              alert("Production totals reset.");
            }}
          >
            Reset Totals
          </button>

        </div>

      </div>

    </div>
  );
}

export default ProductionSummary;